import { Listbox } from '@headlessui/react';
import { memo } from 'react';
import { SpeakerLabelTransitionEffect } from '@goldcast/player-config/src/types/transitions';
import { classnames } from '@/libs/utils';
import Icon from '@/components/atoms/Icon';

interface AnimationOption {
  icon: React.ReactNode;
  value: SpeakerLabelTransitionEffect;
  label: string;
}

const AnimationTypeSelector = ({
  value,
  onChange
}: {
  value: SpeakerLabelTransitionEffect;
  onChange: (value: SpeakerLabelTransitionEffect) => void;
}) => {
  const animations: AnimationOption[] = [
    {
      icon: <Icon name="IconEyeOff" size={16} />,
      value: SpeakerLabelTransitionEffect.None,
      label: 'None'
    },
    {
      icon: <Icon name="IconEye" size={16} />,
      value: SpeakerLabelTransitionEffect.Fade,
      label: 'Fade in'
    },
    {
      icon: <Icon name="IconArrowRightSquare" size={16} />,
      value: SpeakerLabelTransitionEffect.MoveLeft,
      label: 'Move from left'
    },
    {
      icon: <Icon name="IconArrowUpSquare" size={16} />,
      value: SpeakerLabelTransitionEffect.MoveBottom,
      label: 'Move from bottom'
    },
    {
      icon: <Icon name="IconArrowsMaximize" size={16} />,
      value: SpeakerLabelTransitionEffect.Scale,
      label: 'Scale in'
    },
    {
      icon: <Icon name="IconScissors" size={16} />,
      value: SpeakerLabelTransitionEffect.Mask,
      label: 'Mask in'
    }
  ];

  const selectedAnimation = animations.find(animation => animation.value === value) || animations[0];

  return (
    <div className="w-full">
      <Listbox value={value} onChange={onChange}>
        {({ open }) => (
          <>
            <Listbox.Button
              data-testid="animation-selector-button"
              className="flex h-8 w-full items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-card-active-border"
            >
              <span className="flex items-center gap-2 truncate">
                {selectedAnimation.icon}
                {selectedAnimation.label}
              </span>
              <Icon
                name="IconChevronDown"
                size={16}
                className={classnames('transition-transform duration-200', open ? 'rotate-180' : '')}
              />
            </Listbox.Button>
            <Listbox.Options className="absolute z-50 mt-1 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none">
              {animations.map(animation => (
                <Listbox.Option
                  key={animation.value}
                  value={animation.value}
                  data-testid={`animation-option-${animation.value}`}
                  className="cursor-pointer select-none px-3 py-2 hover:bg-gray-50"
                >
                  {({ selected }) => (
                    <div className="flex items-center justify-between">
                      <span
                        className={classnames(
                          'flex items-center gap-2 text-sm',
                          selected ? 'font-semibold text-card-active-border' : 'text-gray-700'
                        )}
                      >
                        {animation.icon}
                        {animation.label}
                      </span>
                      {selected && <Icon name="IconCheck" size={16} className="text-card-active-border" />}
                    </div>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </>
        )}
      </Listbox>
    </div>
  );
};
export default memo(AnimationTypeSelector);
